// A fit segment as stars, which is how the product reads fit everywhere else.
//
// Lifted out of `LeadProfileCard` so the override editor shows the same marks
// rather than a second rendering of the same idea. Three stars for Very Good
// down to a cross for Low; the mapping is fixed to the tier, never derived from
// a score, or two leads in the same segment would carry different ratings.
import React from 'react';

import { Threshold } from '../../../models/ModelPerformances';
import { SEGMENT_LABEL } from '../trees/nodeSegment';
import './fitStars.css';

const STARS: Record<string, number> = {
  'very good': 3,
  good: 2,
  medium: 1,
  low: 0,
};

export default function FitStars({
  segment,
  title,
}: {
  segment: string;
  /** Overrides the tooltip, for callers with something to add to the label. */
  title?: string;
}) {
  const label = SEGMENT_LABEL[segment as Threshold] ?? segment;
  const filled = STARS[segment];
  const tooltip = title ?? label;

  // Low earns a cross rather than an empty row of stars. Three grey outlines
  // read as "not rated yet", which is the one thing Low is not.
  if (filled === 0) {
    return (
      <span className="fits fits--none" title={tooltip}>
        <i className="fas fa-times" aria-hidden="true" />
        <span className="fits-sr">{label}</span>
      </span>
    );
  }

  // An unknown segment shows nothing. Inventing a rating for it would be the
  // one failure mode that cannot be spotted by looking, and the mqa vocabulary
  // (high, very high) lands here.
  if (filled === undefined) return null;

  return (
    <span className="fits" title={tooltip}>
      {[1, 2, 3].map((position) => (
        <i
          key={position}
          className={
            position <= filled
              ? 'fas fa-star fits-star fits-star--on'
              : 'fas fa-star fits-star'
          }
          aria-hidden="true"
        />
      ))}
      <span className="fits-sr">{label}</span>
    </span>
  );
}
